"use client";

import { useEffect, useRef } from "react";
import { useUser } from "@/providers/user-provider";

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

async function waitForTawkLogin(timeoutMs: number = 10_000) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (
      typeof window !== "undefined" &&
      (window.Tawk_API?.login || window.Tawk_API?.setAttributes)
    )
      return true;
    await sleep(200);
  }
  return false;
}

export function TawkAuth() {
  const { user } = useUser();
  const lastUserIdRef = useRef<string | null>(null);
  const role = user?.client?.role;

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Only authenticate CLIENT users
      if (role !== "CLIENT") {
        // If user is admin/manager, logout from Tawk if previously logged in
        if (window.Tawk_API?.logout) {
          window.Tawk_API.logout();
        }
        lastUserIdRef.current = null;
        return;
      }

      if (!user || !user.authUser) {
        lastUserIdRef.current = null;
        // best-effort logout to avoid user leakage across sessions
        window.Tawk_API?.logout?.();
        return;
      }

      const userId = user.authUser.id;
      if (!userId) return;
      if (lastUserIdRef.current === userId) return;

      const ready = await waitForTawkLogin();
      if (!ready || cancelled) return;

      // Get name from client or email from authUser
      const name = user.client?.name ?? user.authUser.email?.split("@")[0] ?? undefined;
      const email = user.authUser.email ?? undefined;

      // For SPAs, this is the most reliable way to update the displayed visitor name/email.
      window.Tawk_API?.setAttributes?.({ name, email, userId }, (error) => {
        if (error) console.error("Tawk setAttributes error:", error);
      });

      window.Tawk_API?.login?.(
        {
          userId,
          name,
          email,
        },
        (error) => {
          if (error) console.error("Tawk login error:", error);
        }
      );

      lastUserIdRef.current = userId;
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user, role]);

  return null;
}
